"use client";

import {
  AccountBalance,
  BusinessCenter,
  Public,
  SupervisedUserCircle,
} from "@mui/icons-material";
import { Box, Card, CardContent, Typography, Stack } from "@mui/material";
import { useMemo } from "react";
import userStatsCountry from "@/_data/dashboard/user_stats_country_091726.json";
import userStatsDaily from "@/_data/dashboard/user_stats_daily_091726.json";
import userStatsPieChart from "@/_data/dashboard/user_stats_piechart_091726.json";
import UserStatisticsPieChart from "@/_components/Analytics/charts/UserStatisticsPieChart";

type UserStatsCountryData = {
  data?: Array<{ country?: string; count?: number }>;
  rowcount?: number;
};

type UserStatsPiechartData = {
  result?: Array<{
    data?: Array<{ asn_type?: string; count?: number }>;
  }>;
};

type UserStatsDailyData = {
  result?: Array<{
    data?: Array<Record<string, number | null>>;
  }>;
};

export type UserMetricCard = {
  label: string;
  value: number | string;
  color: string;
  icon: React.ReactNode;
};

// Create hook to use metricCards in AnalyticsDashboard
export function useUserStatsData() {
  const countryData = userStatsCountry as UserStatsCountryData;
  const pieData = userStatsPieChart as UserStatsPiechartData;
  const dailyData = userStatsDaily as UserStatsDailyData;

  const pieRows = useMemo(
    () => pieData.result?.[0]?.data?.filter((row) => row.asn_type) ?? [],
    [pieData.result],
  );

  const countryCount = useMemo(() => {
    if (Array.isArray(countryData.data)) {
      return countryData.data.length;
    }
    return Number(countryData.rowcount ?? 0);
  }, [countryData.data, countryData.rowcount]);

  const governmentCount = useMemo(
    () => pieRows.find((row) => row.asn_type === "gov")?.count ?? 0,
    [pieRows],
  );

  const businessCount = useMemo(
    () => pieRows.find((row) => row.asn_type === "business")?.count ?? 0,
    [pieRows],
  );

  const averageDailyUsers = useMemo(() => {
    const rows = dailyData.result?.[0]?.data ?? [];
    if (rows.length === 0) return 0;

    const totals = rows.map((row) =>
      Object.entries(row)
        .filter(([key]) => key !== "date")
        .reduce((sum, [, value]) => sum + Number(value ?? 0), 0),
    );

    const totalUsers = totals.reduce((sum, value) => sum + value, 0);
    return Math.round(totalUsers / rows.length);
  }, [dailyData.result]);


  const metricCards = useMemo(
    () => [
      {
        label: "Number of Countries",
        value: countryCount,
        color: "#385bc7",
        icon: <Public sx={{ color: "#5a5d62", fontSize: 22 }} />,
      },

      {
        label: "Number of Businesses",
        value: businessCount,
        color: "#5a0066",
        icon: <BusinessCenter sx={{ color: "#5a5d62", fontSize: 22 }} />,
      },
      {
        label: "Number of Governments",
        value: governmentCount,
        color: "#2e5c18",
        icon: <AccountBalance sx={{ color: "#5a5d62", fontSize: 22 }} />,
      },
      {
        label: "Average Daily Visitors",
        value: (averageDailyUsers>=100 ? averageDailyUsers - (averageDailyUsers % 100) + "+":averageDailyUsers - (averageDailyUsers % 25) + "+"), // display value in 100+ format, or 25+ if not enough
        color: "#017785",
        icon: <SupervisedUserCircle sx={{ color: "#5a5d62", fontSize: 22 }} />,
      },
    ],
    [averageDailyUsers, businessCount, countryCount, governmentCount],
  );
  return { metricCards, pieRows };
}

export default function UserStatisticsPage() {
  const { pieRows } = useUserStatsData();

  return (
    <Stack spacing={2.5}>
      {/* Text Box */}
      <Card sx={{ px: "1rem", py: "1rem" }}>
        <CardContent>
          <Typography sx={{ fontSize: "1rem", color: "#374151" }}>
            Our goal is for the <strong>#PublicDebtIsPublic</strong> platform to
            be used worldwide by government debt managers, investors,
            international organizations, politicians, and ordinary citizens. The
            metrics on this page show the makeup of our current user base, which
            continues to grow.
          </Typography>
        </CardContent>
      </Card>

      {/* Pie Chart */}
      <Box sx={{ mt: 3 }}>
        <UserStatisticsPieChart
          data={pieRows.map((row) => ({
            asn_type: row.asn_type ?? "unknown",
            count: Number(row.count ?? 0),
          }))}
        />
      </Box>
    </Stack>
  );
}
